import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Share2, Clock, ChevronRight, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslatedSettings } from '@/context/SiteSettingsContext';
import Layout from '@/components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

export default function BlogDetailPage() {
  const { slug } = useParams();
  const { language } = useLanguage();
  const { settings } = useTranslatedSettings();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPost();
    loadRelatedPosts();
    // Scroll to top when navigating to blog detail
    window.scrollTo(0, 0);
  }, [slug]);

  const loadPost = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error loading post:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/posts?published=true&limit=3`);
      // Filter out current post
      setRelatedPosts(response.data.filter(p => p.id !== slug).slice(0, 3));
    } catch (error) {
      console.log('Could not load related posts');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate reading time (average 200 words per minute)
  const calculateReadingTime = (text) => {
    if (!text) return 1;
    const words = text.replace(/<[^>]*>/g, '').split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: language === 'en' ? (post.title_en || post.title) : post.title,
          url: url
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(language === 'en' ? 'Link copied!' : 'Link kopiert!');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-theme">
          <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center bg-theme">
          <p className="text-xl text-theme-muted mb-4">
            {language === 'en' ? 'Post not found' : 'Beitrag nicht gefunden'}
          </p>
          <Link to="/blog" className="text-primary hover:underline">
            {language === 'en' ? 'Back to Blog' : 'Zurück zum Blog'}
          </Link>
        </div>
      </Layout>
    );
  }

  const title = language === 'en' ? (post.title_en || post.title) : post.title;
  const content = language === 'en' ? (post.content_en || post.content) : post.content;
  const excerpt = language === 'en' ? (post.excerpt_en || post.excerpt) : post.excerpt;
  const readingTime = calculateReadingTime(content);

  return (
    <Layout>
      <div className="min-h-screen bg-theme" data-testid="blog-detail-page">
        {/* Hero Section */}
        <section className="relative">
          {/* Background Image */}
          <div className="absolute inset-0 h-[50vh] bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a]">
            {post.image_url && (
              <>
                <img
                  src={getImageUrl(post.image_url)}
                  alt={title}
                  className="w-full h-full object-cover opacity-30"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
              </>
            )}
          </div>

          {/* Content */}
          <div className="relative max-w-4xl mx-auto px-6 md:px-12 pt-32 pb-16">
            {/* Back Link */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={18} />
              {language === 'en' ? 'Back to News' : 'Zurück zu News'}
            </Link>

            {/* Category/Tag */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              <span className="inline-block px-3 py-1 bg-gold text-white text-xs font-medium uppercase tracking-wider rounded-full">
                {language === 'en' ? 'News' : 'Neuigkeit'}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-6 leading-tight"
            >
              {title}
            </motion.h1>

            {/* Excerpt */}
            {excerpt && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="text-lg text-white/80 mb-8 max-w-2xl"
              >
                {excerpt}
              </motion.p>
            )}

            {/* Meta Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap items-center gap-6 text-sm text-white/60"
            >
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                {formatDate(post.created_at)}
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} />
                {readingTime} {language === 'en' ? 'min read' : 'Min. Lesezeit'}
              </div>
              {post.author && (
                <div className="flex items-center gap-2">
                  <User size={16} />
                  {post.author}
                </div>
              )}
              <button
                onClick={handleShare}
                className="flex items-center gap-2 hover:text-primary transition-colors ml-auto"
              >
                <Share2 size={16} />
                {language === 'en' ? 'Share' : 'Teilen'}
              </button>
            </motion.div>
          </div>
        </section>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
          {/* Featured Image (if not in hero) */}
          {post.image_url && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12 -mt-24 relative z-10"
            >
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={getImageUrl(post.image_url)}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          )}

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="prose prose-lg max-w-none text-theme
              prose-headings:font-heading prose-headings:text-theme
              prose-p:text-theme-muted prose-p:leading-relaxed
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-theme
              prose-blockquote:border-l-gold prose-blockquote:bg-theme-surface prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-lg
              prose-img:rounded-xl prose-img:shadow-lg"
            dangerouslySetInnerHTML={{ __html: content }}
          />

          {/* Share CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12 pt-8 border-t border-theme"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-theme-muted">
                {language === 'en' 
                  ? 'Found this article helpful? Share it!' 
                  : 'Artikel hilfreich? Teilen Sie ihn!'}
              </p>
              <button
                onClick={handleShare}
                className="btn-secondary flex items-center gap-2"
              >
                <Share2 size={18} />
                {language === 'en' ? 'Share Article' : 'Artikel teilen'}
              </button>
            </div>
          </motion.div>
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="bg-theme-surface py-16">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
              <h2 className="font-heading text-2xl md:text-3xl text-theme mb-8">
                {language === 'en' ? 'More News' : 'Weitere Neuigkeiten'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost, index) => {
                  const relatedTitle = language === 'en' 
                    ? (relatedPost.title_en || relatedPost.title) 
                    : relatedPost.title;
                  
                  return (
                    <motion.div
                      key={relatedPost.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={`/blog/${relatedPost.id}`}
                        className="group block"
                      >
                        <div className="aspect-video rounded-xl overflow-hidden bg-theme mb-4">
                          {relatedPost.image_url ? (
                            <img
                              src={getImageUrl(relatedPost.image_url)}
                              alt={relatedTitle}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gold/10 to-gold/5">
                              <span className="text-4xl text-primary/30">📰</span>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-theme-muted mb-2">
                          {formatDate(relatedPost.created_at)}
                        </p>
                        <h3 className="font-heading text-lg text-theme group-hover:text-primary transition-colors">
                          {relatedTitle}
                        </h3>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              <div className="text-center mt-8">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  {language === 'en' ? 'View all news' : 'Alle Neuigkeiten'}
                  <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-[#0A0A0A] to-[#1a1a1a] text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-heading text-2xl md:text-3xl mb-4">
              {language === 'en' 
                ? 'Looking for your dream car?' 
                : 'Auf der Suche nach Ihrem Traumwagen?'}
            </h2>
            <p className="text-white/70 mb-8 max-w-xl mx-auto">
              {language === 'en'
                ? 'Browse our exclusive collection of premium vehicles.'
                : 'Entdecken Sie unsere exklusive Auswahl an Premium-Fahrzeugen.'}
            </p>
            <Link
              to="/cars"
              className="btn-primary inline-flex items-center gap-2"
            >
              {language === 'en' ? 'View Vehicles' : 'Fahrzeuge ansehen'}
              <ChevronRight size={18} />
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}
